import { useQuery } from '@tanstack/react-query';
import { axiosGetV3, axiosPostV3 } from '../../api/axios-handler';
import { StickyStateKeys } from '../utils/useStickyState';

/**
 * Lightweight descriptor for the recent-chats list. The transcript itself is
 * fetched lazily via {@link getPeersonaThread} when a thread is opened.
 */
export interface PeersonaThreadSummary {
  thread_id: number;
  created_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  message_count: number;
  /** Derived from the first user message — the de-facto chat title. */
  title?: string | null;
  /** Derived from the most recent message — a one-line preview. */
  preview?: string | null;
  last_role?: 'user' | 'assistant' | string | null;
}

/** Full thread payload returned when restoring / opening a past chat. */
export interface PeersonaThreadDetail {
  thread_id: number;
  message_history: PeersonaMessageRow[];
  created_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
}

export interface PeersonaChunkInfo {
  id: number;
  source_type: string;
  source_ref_id?: number | null;
  score: number;
  content_preview: string;
}

/**
 * Cross-content referral payload. When the Peersona's own corpus has no match
 * for the user's message, the orchestrator searches platform listener_audio
 * via Algolia and returns the best non-self hit here.
 *
 * ``items`` holds the raw Algolia hit in the same Content shape Fi's
 * ``explore_content`` tool emits, so the frontend can render it via
 * ``<FiStoryResults>`` — matching the rest of the platform's tile UX. The
 * flat ``listener_*`` / ``audio_*`` fields are kept for back-compat with
 * older message rows persisted before items[] landed and for any
 * non-tile rendering (e.g. dev tooling, eval reports).
 */
export interface PeersonaReferralInfo {
  listener_role_id: number;
  listener_name: string;
  audio_id: number;
  audio_title: string;
  audio_excerpt?: string;
  audio_url?: string | null;
  media_type?: string | null;
  profile_photo_url?: string | null;
  /**
   * Single-element array of the Algolia hit (or empty if back-end could
   * not surface the unprojected item). Frontend hands this to
   * ``<FiStoryResults>`` verbatim.
   */
  items?: Record<string, unknown>[];
}

/**
 * Safety resource attached to a crisis-flagged response. Sourced from the
 * Algolia `resources` index (hotlines, articles, services). Rendered as a
 * card visually attached beneath the assistant bubble; the model's reply
 * acknowledges that "info is below this message" so the card doesn't feel
 * disconnected from the conversation.
 *
 * Fields are heterogeneous because the underlying resources are
 * heterogeneous — a hotline has a phone, an article has a URL. The
 * frontend renders whichever CTAs are populated.
 */
export interface PeersonaSafetyResource {
  id?: number | string;
  title: string;
  description?: string | null;
  url?: string | null;
  phone?: string | null;
  source?: string;
}

/**
 * Concierge-handoff payload. Emitted when the orchestrator detects that the
 * user is asking for something only Fi can do (connect with a listener,
 * schedule a call, search the platform). The Peersona stays in its lane,
 * delivers a templated acknowledgment in ``ack``, and includes the user's
 * original request so the frontend can forward it to Fi without making the
 * user retype.
 *
 * ``subtype`` mirrors the classifier output and only selects the CTA label
 * (e.g. "Continue in Fi" vs "Schedule in Fi"). The handoff is always
 * user-initiated — tapping the button switches to Fi with the message
 * pre-loaded; nothing auto-switches.
 */
export interface PeersonaHandoffInfo {
  target: 'fi';
  subtype: 'connect_listener' | 'schedule_call' | 'find_content' | 'default' | string;
  original_message: string;
  ack: string;
}

/**
 * One entry in the audio catalog surfaced by the referral_followup branch.
 * ``relevance`` is the orchestrator's ranking score (higher = better fit for
 * the user's follow-up question), which the UI can use to highlight the
 * primary CTA or fade the long-tail entries.
 */
export interface PeersonaPeerLookupAudio {
  audio_id: number;
  title: string;
  summary: string;
  relevance: number;
}

/**
 * Peer-lookup payload. Emitted when the user follows up on a previously
 * referred peer ("what did she go through?", "tell me more about him"). The
 * orchestrator resolves the antecedent via prior referrals in history,
 * pulls the peer's bio + ranked audio catalog, and answers in third-person
 * librarian voice.
 *
 * ``items`` holds the ranked catalog in the same Content shape Fi's
 * ``explore_content`` tool emits, so the carousel under the bubble renders
 * via the shared ``<FiStoryResults>`` widget — same tile UX as Fi's search
 * results, no Peersona-specific tile components needed. ``audio_catalog``
 * stays for structured per-entry metadata (relevance scores, summaries)
 * that the tile rendering doesn't need but tooling / persistence does.
 */
export interface PeersonaPeerLookupInfo {
  listener_role_id: number;
  peer_name: string;
  /** Display string like "She / Her" or "He / Him"; render verbatim. */
  peer_pronoun: string;
  audio_catalog: PeersonaPeerLookupAudio[];
  /**
   * Ranked Content-shaped catalog entries (most relevant first). Frontend
   * hands this to ``<FiStoryResults>`` verbatim. Same length and order as
   * ``audio_catalog``, minus any entries where the back-end couldn't
   * build a usable tile dict.
   */
  items?: Record<string, unknown>[];
}

export interface PeersonaToolResult {
  tool: string;
  result: {
    tier?: string;
    top_score?: number;
    chunks?: PeersonaChunkInfo[];
    referral?: PeersonaReferralInfo;
    safety_resources?: PeersonaSafetyResource[];
    intent?: string;
    handoff?: PeersonaHandoffInfo;
    peer_lookup?: PeersonaPeerLookupInfo;
  };
}

export interface PeersonaMessageRow {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  peersona?: {
    tier?: string;
    top_score?: number;
    chunks?: PeersonaChunkInfo[];
    referral?: PeersonaReferralInfo;
    safety_resources?: PeersonaSafetyResource[];
    intent?: string;
    handoff?: PeersonaHandoffInfo;
    peer_lookup?: PeersonaPeerLookupInfo;
  };
}

export interface PeersonaChatResponse {
  thread_id: number;
  ai_response: string;
  tool_results: PeersonaToolResult[];
  message_history: PeersonaMessageRow[];
  crisis?: {
    crisis_detected?: boolean;
    emergency?: boolean;
    selfharm?: boolean;
  } | null;
}

export interface PeersonaChatBotMeta {
  id: number;
  name: string;
  display_name?: string | null;
  welcome_message?: string | null;
  avatar_file_path?: string | null;
  /**
   * One-sentence description of what this Peersona is for. Rendered on
   * Peersona tiles (peer profile, post-audio prompt) and as the subtitle of
   * the chat header. Authored manually for v1; later AI- or admin-suggested.
   */
  scope_line?: string | null;
  processing_status?: string | null;
  /** Trait pills for the About drawer when populated by the backend. */
  traits?: string[] | null;
  /** Vimeo embed ref for welcome intro video (10-digit id or id?h=hash). */
  welcome_video_vimeo_id?: string | null;
}

export const peersonaKeys = {
  all: ['peersona'] as const,
  chatbot: (id: number) => [...peersonaKeys.all, 'chatbot', id] as const,
  threads: (id: number) => [...peersonaKeys.all, 'threads', id] as const,
};

/** GET /api/v3/peersona/<chatbot_id> — metadata for the header / welcome card. */
export const usePeersonaChatBot = (chatbotId: number | null, enabled = true) =>
  useQuery({
    queryKey: peersonaKeys.chatbot(chatbotId ?? -1),
    queryFn: async () => {
      if (chatbotId == null) return null;
      const res = await axiosGetV3(`/peersona/${chatbotId}`, {});
      return res.data?.chatbot as PeersonaChatBotMeta | null;
    },
    enabled: enabled && chatbotId != null,
    staleTime: 5 * 60_000,
  });

/**
 * GET /api/v3/peersona/<id>/threads — the current user's recent chats with
 * this Peersona (newest activity first). Powers the history dropdown.
 */
export const usePeersonaThreads = (chatbotId: number | null, enabled = true, limit = 5) =>
  useQuery({
    queryKey: peersonaKeys.threads(chatbotId ?? -1),
    queryFn: async () => {
      if (chatbotId == null) return [] as PeersonaThreadSummary[];
      const res = await axiosGetV3(`/peersona/${chatbotId}/threads`, { limit });
      return (res.data?.threads ?? []) as PeersonaThreadSummary[];
    },
    enabled: enabled && chatbotId != null,
    staleTime: 30_000,
  });

/** GET /api/v3/peersona/<id>/threads/<thread_id> — one thread's full transcript. */
export async function getPeersonaThread(chatbotId: number, threadId: number): Promise<PeersonaThreadDetail> {
  const res = await axiosGetV3(`/peersona/${chatbotId}/threads/${threadId}`, {});
  return res.data as PeersonaThreadDetail;
}

/** Non-streaming chat. Useful for fallback or programmatic clients. */
export async function postPeersonaChat(
  chatbotId: number,
  body: { message: string; thread_id?: number | null },
): Promise<PeersonaChatResponse> {
  const res = await axiosPostV3(`/peersona/${chatbotId}/chat`, body);
  return res.data;
}

/** POST /api/v3/peersona/<id>/end — close the thread (mirrors Fi reset). */
export async function postPeersonaEnd(chatbotId: number, threadId: number): Promise<void> {
  await axiosPostV3(`/peersona/${chatbotId}/end`, { thread_id: threadId });
}

// ---------------------------------------------------------------------------
// Streaming chat (SSE over fetch — EventSource can't carry the Bearer token).
// ---------------------------------------------------------------------------

export type PeersonaStreamEvent =
  | { type: 'stage'; stage: string; message: string; top_score?: number; intent?: string }
  | { type: 'token'; text: string }
  | { type: 'crisis'; crisis_detected?: boolean; emergency?: boolean; selfharm?: boolean }
  | { type: 'safety_resources'; resources: PeersonaSafetyResource[] }
  // Concierge-handoff event. Arrives BEFORE 'done' on short-circuit turns so
  // the UI can render the manual handoff CTA before the bubble finishes
  // streaming. The same payload is also attached to 'done' for the canonical
  // persistence write.
  | ({ type: 'handoff' } & PeersonaHandoffInfo)
  | {
      type: 'done';
      thread_id: number;
      ai_response: string;
      tier: string;
      top_score: number;
      chunks: PeersonaChunkInfo[];
      referral?: PeersonaReferralInfo;
      safety_resources?: PeersonaSafetyResource[];
      intent?: string;
      handoff?: PeersonaHandoffInfo;
      peer_lookup?: PeersonaPeerLookupInfo;
    }
  | { type: 'error'; message: string };

export interface StreamPeersonaChatOptions {
  chatbotId: number;
  message: string;
  threadId?: number | null;
  signal?: AbortSignal;
  onEvent: (event: PeersonaStreamEvent) => void;
}

function buildStreamUrl(chatbotId: number): string {
  const domain = process.env.REACT_APP_DOMAIN_NAME ?? '';
  const v3 = process.env.REACT_APP_API_VERSION3 ?? '/api/v3';
  return `${domain}${v3}/peersona/${chatbotId}/chat/stream`;
}

function getAuthHeader(): Record<string, string> {
  try {
    const token =
      localStorage.getItem(StickyStateKeys.AuthorizationToken) ?? localStorage.getItem('authorization_guest_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * Open an SSE connection to /peersona/<id>/chat/stream and dispatch events.
 *
 * Returns a promise that resolves when the stream closes cleanly (or rejects
 * on network/abort errors). Callers wire ``onEvent`` to update UI state and
 * use ``signal`` to cancel mid-stream.
 */
export async function streamPeersonaChat({
  chatbotId,
  message,
  threadId,
  signal,
  onEvent,
}: StreamPeersonaChatOptions): Promise<void> {
  const response = await fetch(buildStreamUrl(chatbotId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // The Flask check_request middleware rejects any /api/* request whose
      // Accept "best" isn't application/json. We still want SSE for success
      // responses (server forces text/event-stream Content-Type regardless),
      // so list JSON first to satisfy the check while keeping event-stream
      // as a secondary preference.
      Accept: 'application/json, text/event-stream',
      'X-Requested-With': 'XMLHTTPRequest',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ message, thread_id: threadId ?? undefined }),
    credentials: 'include',
    signal,
  });

  if (!response.ok || !response.body) {
    let detail = '';
    try {
      const raw = await response.text();
      try {
        const parsed = JSON.parse(raw);
        detail = typeof parsed === 'string' ? parsed : parsed?.description || parsed?.message || JSON.stringify(parsed);
      } catch {
        detail = raw;
      }
    } catch {
      /* ignore */
    }
    throw new Error(`Peersona stream failed: ${response.status} ${response.statusText} ${detail}`.trim());
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  const dispatchFrame = (eventType: string, dataPayload: string) => {
    let parsed: unknown = {};
    try {
      parsed = dataPayload ? JSON.parse(dataPayload) : {};
    } catch {
      parsed = { raw: dataPayload };
    }
    onEvent({ type: eventType, ...(parsed as Record<string, unknown>) } as PeersonaStreamEvent);
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        if (!frame.trim() || frame.startsWith(':')) continue;

        let eventType = 'message';
        const dataLines: string[] = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trimStart());
          }
        }
        dispatchFrame(eventType, dataLines.join('\n'));
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}
